/**
 * Outbox de sincronização (doc 12.2): buffer durável de operações criadas
 * offline. Cada operação tem `operacaoId` único → o push é idempotente no
 * servidor, então reenviar é seguro. Persiste pela porta `KeyValueStore`.
 */
import type { EscopoData, OperacaoSyncDTO } from "@caint/shared";
import type { KeyValueStore } from "../persistence/port.ts";
import { type ApiClient, ApiError } from "./api.ts";

const CHAVE = "caint-sync-outbox-v1";
/** Operações que o servidor recusou por erro determinístico (4xx) — isoladas aqui. */
const QUARENTENA = "caint-sync-quarentena-v1";
/**
 * Operações RECUSADAS pelo servidor por "ciclo encerrado / fora da janela"
 * (`rejeitada`). NÃO são dado inválido — são edições válidas que o ciclo ainda
 * não aceita. Antes eram DESCARTADAS (perda silenciosa: o tablet voltava a dizer
 * "sincronizado"). Agora ficam retidas aqui, contam como pendentes e o auto-sync
 * as re-tenta sozinho — quando a janela/ciclo reabrir, sobem.
 */
const RETIDAS = "caint-sync-retidas-v1";

/**
 * Operações por push. O servidor valida `operacoes.max(50)`; enviar a fila
 * inteira de uma vez (ex.: 323 acumuladas offline) dava HTTP 400 e travava o
 * sync para sempre. O flush agora envia em lotes deste tamanho (folga sob 50).
 */
export const LOTE_PUSH = 25;

type Respostas = OperacaoSyncDTO["respostas"];
export interface ExtrasOperacao {
  readonly qaciFinal?: number;
  readonly ims?: number;
  /** Escopo do ciclo — sincroniza junto (doc 17b §6). Enfileirado como op própria. */
  readonly escopo?: EscopoData;
}

/** Resultado de um flush: o que foi aceito, o que o servidor recusou e o que sobrou. */
export interface ResultadoFlush {
  /** Operações aceitas (aplicada) ou já vistas (duplicada). */
  readonly confirmadas: number;
  /** Operações recusadas neste flush (ciclo encerrado/fora da janela) — foram RETIDAS. */
  readonly rejeitadas: number;
  /** Operações venenosas (4xx determinístico) isoladas em quarentena — não travam mais a fila. */
  readonly descartadas: number;
  /** Total de operações retidas (aguardando o ciclo aceitar) após o flush. */
  readonly retidas: number;
  /** Operações ainda pendentes na fila ativa após o flush (0 = fila vazia). */
  readonly pendentes: number;
}

export class SyncOutbox {
  constructor(
    private readonly store: KeyValueStore,
    private readonly gerarId: () => string = () => crypto.randomUUID(),
    private readonly agora: () => string = () => new Date().toISOString(),
  ) {}

  /** Enfileira uma operação e a devolve. */
  enqueue(avaliacaoId: string, respostas: Respostas, extras: ExtrasOperacao = {}): OperacaoSyncDTO {
    const op: OperacaoSyncDTO = {
      operacaoId: this.gerarId(),
      avaliacaoId,
      respostas,
      atualizadoEm: this.agora(),
      ...(extras.qaciFinal !== undefined ? { qaciFinal: extras.qaciFinal } : {}),
      ...(extras.ims !== undefined ? { ims: extras.ims } : {}),
      // `EscopoData` é `readonly`; o DTO de sync usa arrays mutáveis (mesma forma).
      ...(extras.escopo !== undefined ? { escopo: extras.escopo as OperacaoSyncDTO["escopo"] } : {}),
    };
    this.store.write(CHAVE, [...this.pending(), op]);
    return op;
  }

  pending(): OperacaoSyncDTO[] {
    return this.store.read<OperacaoSyncDTO[]>(CHAVE, []);
  }

  remover(ids: readonly string[]): void {
    const alvos = new Set(ids);
    this.store.write(
      CHAVE,
      this.pending().filter((op) => !alvos.has(op.operacaoId)),
    );
  }

  /** Operações isoladas por veneno (4xx) — para inspeção/diagnóstico. */
  quarentena(): OperacaoSyncDTO[] {
    return this.store.read<OperacaoSyncDTO[]>(QUARENTENA, []);
  }

  /** Operações retidas (recusadas por ciclo encerrado/fora da janela) — reenviáveis. */
  retidas(): OperacaoSyncDTO[] {
    return this.store.read<OperacaoSyncDTO[]>(RETIDAS, []);
  }

  /** Pendências reais que ainda NÃO chegaram ao servidor: fila ativa + retidas. */
  totalPendente(): number {
    return this.pending().length + this.retidas().length;
  }

  /** Move ops recusadas (`rejeitada`) para o balde de retidas (saem da fila ativa). */
  private reter(ids: readonly string[]): void {
    if (ids.length === 0) {
      return;
    }
    const alvo = new Set(ids);
    const presas = this.pending().filter((op) => alvo.has(op.operacaoId));
    if (presas.length > 0) {
      this.store.write(RETIDAS, [...this.retidas(), ...presas]);
    }
    this.remover(ids);
  }

  /** Devolve as retidas à fila ativa para uma nova tentativa (chamado a cada flush). */
  reprocessarRetidas(): void {
    const retidas = this.retidas();
    if (retidas.length === 0) {
      return;
    }
    this.store.write(CHAVE, [...this.pending(), ...retidas]);
    this.store.write(RETIDAS, []);
  }

  /**
   * Funde patches do MESMO item (avaliação+item) numa única operação, com o
   * último valor de cada campo. Reduz muito o volume (vários toques no mesmo
   * item viram uma op só) e é seguro: o backend já mescla campo a campo. Só
   * reescreve a fila quando há de fato o que fundir (evita churn de id/ordem).
   */
  private coalescer(): void {
    const grupos = new Map<string, OperacaoSyncDTO[]>();
    const passantes: OperacaoSyncDTO[] = []; // ops fora do formato 1-item: mantidas como estão
    const ordem: string[] = [];
    for (const op of this.pending()) {
      const item = op.respostas.length === 1 ? op.respostas[0] : undefined;
      if (!item) {
        passantes.push(op);
        continue;
      }
      const chave = `${op.avaliacaoId}\u001f${item.itemId}`;
      const g = grupos.get(chave);
      if (g) {
        g.push(op);
      } else {
        grupos.set(chave, [op]);
        ordem.push(chave);
      }
    }
    if (![...grupos.values()].some((g) => g.length > 1)) {
      return; // nada repetido — não mexe na fila
    }
    const novo: OperacaoSyncDTO[] = [];
    for (const chave of ordem) {
      const g = grupos.get(chave);
      const primeiro = g?.[0];
      if (!g || !primeiro) {
        continue;
      }
      if (g.length === 1) {
        novo.push(primeiro); // op única: preserva id (sem churn)
        continue;
      }
      let resposta = {} as Respostas[number];
      let qaciFinal: number | undefined;
      let ims: number | undefined;
      let atualizadoEm = primeiro.atualizadoEm;
      for (const op of g) {
        resposta = { ...resposta, ...op.respostas[0] }; // último valor por campo vence
        if (op.qaciFinal !== undefined) {
          qaciFinal = op.qaciFinal;
        }
        if (op.ims !== undefined) {
          ims = op.ims;
        }
        atualizadoEm = op.atualizadoEm;
      }
      novo.push({
        operacaoId: this.gerarId(),
        avaliacaoId: primeiro.avaliacaoId,
        respostas: [resposta],
        atualizadoEm,
        ...(qaciFinal !== undefined ? { qaciFinal } : {}),
        ...(ims !== undefined ? { ims } : {}),
      });
    }
    this.store.write(CHAVE, [...novo, ...passantes]);
  }

  /** Move ops para a quarentena (saem da fila ativa, viram diagnóstico). */
  private quarentenar(ids: readonly string[]): void {
    if (ids.length === 0) {
      return;
    }
    const alvo = new Set(ids);
    const presas = this.pending().filter((op) => alvo.has(op.operacaoId));
    if (presas.length > 0) {
      this.store.write(QUARENTENA, [...this.quarentena(), ...presas]);
    }
    this.remover(ids);
  }

  /**
   * Envia um lote; em erro 4xx (determinístico) BISSECTA o lote para isolar a op
   * venenosa, devolvendo-a como `descartadas` (sem travar as boas). Erro de rede
   * ou 5xx (transitório) é propagado — o lote fica para a próxima tentativa.
   */
  private async enviarLote(
    client: ApiClient,
    token: string,
    lote: readonly OperacaoSyncDTO[],
  ): Promise<{ aceitas: string[]; recusadas: string[]; descartadas: string[] }> {
    try {
      const resultados = await client.push(lote, token);
      return {
        aceitas: resultados
          .filter((r) => r.status === "aplicada" || r.status === "duplicada")
          .map((r) => r.operacaoId),
        recusadas: resultados.filter((r) => r.status === "rejeitada").map((r) => r.operacaoId),
        descartadas: [],
      };
    } catch (e) {
      const transitorio = !(e instanceof ApiError) || e.status >= 500;
      if (transitorio) {
        throw e; // rede/5xx: tenta de novo depois, fila intacta
      }
      if (lote.length <= 1) {
        // 4xx num único item → veneno: isola para a fila não travar.
        return { aceitas: [], recusadas: [], descartadas: lote.map((o) => o.operacaoId) };
      }
      const meio = Math.floor(lote.length / 2);
      const a = await this.enviarLote(client, token, lote.slice(0, meio));
      const b = await this.enviarLote(client, token, lote.slice(meio));
      return {
        aceitas: [...a.aceitas, ...b.aceitas],
        recusadas: [...a.recusadas, ...b.recusadas],
        descartadas: [...a.descartadas, ...b.descartadas],
      };
    }
  }

  /**
   * Funde patches repetidos e envia EM LOTES de {@link LOTE_PUSH} (o servidor
   * recusa pushes com mais de 50 operações). A cada lote: remove confirmadas
   * (aplicada/duplicada) e rejeitadas (ciclo encerrado), e isola venenosas (4xx)
   * em quarentena. Propaga erro de rede/HTTP; o progresso já feito permanece.
   */
  async flush(client: ApiClient, token: string): Promise<ResultadoFlush> {
    // Retidas voltam à fila para nova tentativa: quando o ciclo reabre/entra na
    // janela, elas sobem sozinhas neste flush; senão, são retidas de novo abaixo.
    this.reprocessarRetidas();
    this.coalescer();
    let confirmadas = 0;
    let rejeitadas = 0;
    let descartadas = 0;
    while (true) {
      const lote = this.pending().slice(0, LOTE_PUSH);
      if (lote.length === 0) {
        break;
      }
      const res = await this.enviarLote(client, token, lote);
      this.quarentenar(res.descartadas);
      this.reter(res.recusadas); // recusadas por ciclo → retidas (não descarta)
      this.remover(res.aceitas); // só as aceitas saem de vez
      confirmadas += res.aceitas.length;
      rejeitadas += res.recusadas.length;
      descartadas += res.descartadas.length;
      // Trava de segurança: se nada saiu (status inesperado), evita loop infinito.
      if (res.aceitas.length + res.recusadas.length + res.descartadas.length === 0) {
        break;
      }
    }
    return {
      confirmadas,
      rejeitadas,
      descartadas,
      retidas: this.retidas().length,
      pendentes: this.pending().length,
    };
  }
}
